/**
 * DOM rendering functions extracted from family.js.
 * All DOM operations target a provided container element (not document.body).
 */
import {
  isPerson,
  isUnion,
  getLeftUnion,
  getRightUnion,
  getAboveUnion,
  getChildren,
  getRenderedChildren,
  displayName,
} from './treeLayout'

function showDiv(div, displayMode = false) {
  if (displayMode) {
    div.style.display = 'block'
  } else {
    div.style.visibility = ''
  }
}

function hideDiv(div, displayMode = false) {
  if (displayMode) {
    div.style.display = 'none'
  } else {
    div.style.visibility = 'hidden'
  }
}

export function makeDiv(name, entries, neighbours, container, onClickPerson, photoDir = 'photos/') {
  const result = document.createElement('div')
  const rawName = name
  result.onclick = function () {
    if (onClickPerson) onClickPerson(rawName)
  }
  result.className = 'label'
  const lines = displayName(name).replace('-', '\u2011').split(' ')
  const nameDiv = document.createElement('div')
  for (let i = 0; i < lines.length; i++) {
    if (i > 0) nameDiv.appendChild(document.createElement('br'))
    nameDiv.appendChild(document.createTextNode(lines[i]))
  }
  result.appendChild(nameDiv)

  let lifespanDiv = null
  let photoDiv = null
  const info = []

  if (entries[name]) {
    for (const data of entries[name]) {
      if (data.startsWith('l: ')) {
        lifespanDiv = document.createElement('div')
        const [birth, death] = data.substring(3).split('-')
        if (birth !== '') {
          lifespanDiv.appendChild(
            document.createTextNode(birth + (death === '' ? '\u2013' : '')),
          )
        }
        if (birth !== '' && death !== '') {
          lifespanDiv.appendChild(document.createElement('br'))
        }
        if (death !== '') {
          lifespanDiv.appendChild(document.createTextNode('\u2013' + death))
        }
        lifespanDiv.className = 'lifespan'
      }
      if (data.startsWith('p: ')) {
        const img = document.createElement('img')
        const photoPath = data.substring(3)
        // Use full URL if it starts with http(s), otherwise use photoDir prefix
        img.src = photoPath.match(/^https?:\/\//) ? photoPath : photoDir + photoPath
        img.style.width = '70px'
        const imgWrapper = document.createElement('div')
        imgWrapper.appendChild(img)
        photoDiv = imgWrapper
      }
      if (data.startsWith('n: ')) {
        info.push(data.substring(3))
      }
    }
  }

  function addMarriageInfo(partner, union) {
    let result = ''
    for (const data of entries[union]) {
      if (data.startsWith('n: ')) {
        result += data.substring(3)
      }
    }
    if (result.length === 0) return
    info.push('With ' + displayName(partner) + ': ' + result)
  }

  const leftUnion = getLeftUnion(name, neighbours)
  if (leftUnion !== null) addMarriageInfo(leftUnion.split(' + ')[0], leftUnion)
  const rightUnion = getRightUnion(name, neighbours)
  if (rightUnion !== null) addMarriageInfo(rightUnion.split(' + ')[1], rightUnion)

  if (photoDiv !== null) result.appendChild(photoDiv)
  if (lifespanDiv !== null) result.appendChild(lifespanDiv)

  if (info.length !== 0) {
    result.classList.add('has-info')
  }

  // Attach to container for size computation
  container.appendChild(result)
  result.style.top = '200px'
  result.style.left = '200px'
  hideDiv(result)

  // Store info on the element for the info pane
  result._treeInfo = { name: displayName(name), info }

  return result
}

export function makeDivs(entries, neighbours, container, onClickPerson, photoDir) {
  const result = {}
  for (const name of Object.keys(neighbours)) {
    if (isPerson(name)) {
      result[name] = makeDiv(name, entries, neighbours, container, onClickPerson, photoDir)
    }
  }
  return result
}

export function placeDiv(div, x, y) {
  showDiv(div)
  div.style.top = y - div.offsetHeight / 2 + 'px'
  div.style.left = x - div.offsetWidth / 2 + 'px'
}

export function createLine(x1, y1, x2, y2, lineClass) {
  function createLineElement(x, y, length, angle) {
    const line = document.createElement('div')
    const styles =
      'border-style: solid; ' +
      'width: ' + length + 'px; ' +
      'height: 0px; ' +
      '-moz-transform: rotate(' + angle + 'rad); ' +
      '-webkit-transform: rotate(' + angle + 'rad); ' +
      '-o-transform: rotate(' + angle + 'rad); ' +
      '-ms-transform: rotate(' + angle + 'rad); ' +
      'position: absolute; ' +
      'top: ' + y + 'px; ' +
      'left: ' + x + 'px; '
    line.setAttribute('style', styles)
    line.classList.add('drawn-line')
    line.classList.add(lineClass)
    return line
  }

  const a = x1 - x2
  const b = y1 - y2
  const c = Math.sqrt(a * a + b * b)
  const sx = (x1 + x2) / 2
  const sy = (y1 + y2) / 2
  const x = sx - c / 2
  const y = sy
  const alpha = Math.PI - Math.atan2(-b, a)
  return createLineElement(x, y, c, alpha)
}

export function drawLine(container, p, q, lineClass) {
  container.appendChild(createLine(p.x, p.y, q.x, q.y, lineClass))
}

function connect(container, node1, node2, layout, neighbours, divs, lineClass) {
  const [person, union] = isPerson(node1) ? [node1, node2] : [node2, node1]
  if (union.split(' + ').includes(person)) {
    if (getRenderedChildren(union, neighbours, layout).length > 0) {
      const fudge = 4
      drawLine(
        container,
        {
          x: layout[person].x,
          y: layout[person].y + divs[person].offsetHeight / 2 - fudge,
        },
        { x: layout[union].x, y: layout[union].y },
        lineClass,
      )
    } else {
      const isLeft = union.split(' + ')[0] === person
      drawLine(
        container,
        {
          x: layout[person].x + ((isLeft ? 1 : -1) * divs[person].offsetWidth) / 2,
          y: layout[person].y,
        },
        { x: layout[union].x, y: layout[union].y },
        lineClass,
      )
    }
  } else {
    drawLine(
      container,
      {
        x: layout[person].x,
        y: layout[person].y - divs[person].offsetHeight / 2,
      },
      { x: layout[union].x, y: layout[union].y },
      lineClass,
    )
  }
}

export function traverse(
  container,
  name,
  pred,
  neighbours,
  divs,
  layout,
  mode,
  flags = { ancestor: true, descendant: true, blood: true },
) {
  let posClass
  if (pred === null) {
    posClass = 'pos-root'
  } else if (flags.ancestor) {
    posClass = 'pos-ancestor'
  } else if (flags.descendant) {
    posClass = 'pos-descendant'
  } else if (flags.blood) {
    posClass = 'pos-blood'
  } else {
    posClass = 'pos-other'
  }

  if (
    mode === 'drawConnections' &&
    layout.hasOwnProperty(name) &&
    layout.hasOwnProperty(pred)
  ) {
    if (
      isUnion(name) &&
      name.split(' + ').includes(pred) &&
      getRenderedChildren(name, neighbours, layout).length === 0
    ) {
      connect(container, name, pred, layout, neighbours, divs, 'pos-other')
    } else {
      connect(container, name, pred, layout, neighbours, divs, posClass)
    }
  }

  function recur(newName, newFlags) {
    if (newName === null || newName === pred) return
    traverse(
      container,
      newName,
      name,
      neighbours,
      divs,
      layout,
      mode,
      Object.assign({}, flags, newFlags),
    )
  }

  if (isPerson(name)) {
    if (mode === 'setPeopleClasses') {
      divs[name].classList.add(posClass)
    }
    const leftUnion = getLeftUnion(name, neighbours)
    recur(leftUnion, { ancestor: false, blood: flags.ancestor || flags.blood })
    const rightUnion = getRightUnion(name, neighbours)
    recur(rightUnion, { ancestor: false, blood: flags.ancestor || flags.blood })
    const aboveUnion = getAboveUnion(name, neighbours)
    recur(aboveUnion, { blood: false, descendant: false })
  } else {
    const [p1, p2] = name.split(' + ')
    recur(p1, { blood: false, descendant: false })
    recur(p2, { blood: false, descendant: false })
    for (const child of getChildren(name, neighbours)) {
      recur(child, { ancestor: false, blood: flags.ancestor || flags.blood })
    }
  }
}

export function setPeopleClasses(container, rootName, neighbours, divs) {
  traverse(container, rootName, null, neighbours, divs, null, 'setPeopleClasses')
}

export function drawConnections(container, rootName, neighbours, divs, layout) {
  traverse(container, rootName, null, neighbours, divs, layout, 'drawConnections')
}
